import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { GlobeIcon, PlusIcon, SearchIcon, FilterIcon, ChevronDownIcon, EditIcon, TrashIcon, EyeIcon, BuildingIcon, BookOpenIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
const EligibleLists: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('universities');
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('All Regions');
  const [statusFilter, setStatusFilter] = useState('All');
  // Sample universities data
  const universities = [{
    id: 'uni001',
    name: 'Massachusetts Institute of Technology',
    country: 'United States',
    region: 'North America',
    ranking: 1,
    status: 'Approved',
    programs: 45,
    lastUpdated: '2023-06-15',
    accreditation: 'ABET, AACSB'
  }, {
    id: 'uni002',
    name: 'Stanford University',
    country: 'United States',
    region: 'North America',
    ranking: 2,
    status: 'Approved',
    programs: 38,
    lastUpdated: '2023-06-10',
    accreditation: 'ABET, AACSB'
  }, {
    id: 'uni003',
    name: 'University of Cambridge',
    country: 'United Kingdom',
    region: 'Europe',
    ranking: 3,
    status: 'Approved',
    programs: 42,
    lastUpdated: '2023-06-12',
    accreditation: 'QAA'
  }, {
    id: 'uni004',
    name: 'ETH Zurich',
    country: 'Switzerland',
    region: 'Europe',
    ranking: 8,
    status: 'Under Review',
    programs: 28,
    lastUpdated: '2023-06-18',
    accreditation: 'AAQ'
  }];
  // Sample fields of study data
  const fieldsOfStudy = [{
    id: 'field001',
    name: 'Computer Science',
    category: 'Technology',
    status: 'Approved',
    priority: 'High',
    description: 'Computing, software development, artificial intelligence',
    eligibleUniversities: 25,
    lastUpdated: '2023-06-15'
  }, {
    id: 'field002',
    name: 'Medicine',
    category: 'Healthcare',
    status: 'Approved',
    priority: 'Critical',
    description: 'Medical studies, healthcare, research',
    eligibleUniversities: 18,
    lastUpdated: '2023-06-10'
  }, {
    id: 'field003',
    name: 'Renewable Energy Engineering',
    category: 'Engineering',
    status: 'Approved',
    priority: 'High',
    description: 'Sustainable energy, environmental engineering',
    eligibleUniversities: 22,
    lastUpdated: '2023-06-12'
  }, {
    id: 'field004',
    name: 'Artificial Intelligence',
    category: 'Technology',
    status: 'Under Review',
    priority: 'High',
    description: 'Machine learning, deep learning, AI research',
    eligibleUniversities: 15,
    lastUpdated: '2023-06-18'
  }];
  const regionOptions = ['All Regions', 'North America', 'Europe', 'Asia', 'Oceania'];
  const statusOptions = ['All', 'Approved', 'Under Review', 'Suspended'];
  const getStatusColor = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getPriorityColor = priority => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-800';
      case 'High':
        return 'bg-orange-100 text-orange-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  return <Layout title="Eligible Universities & Fields" persona="MoHESR Admin" data-id="element-2723">
      <div className="max-w-7xl mx-auto" data-id="element-2724">
        {/* Header with stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-2725">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2726">
            <div className="flex items-start justify-between" data-id="element-2727">
              <div data-id="element-2728">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2729">
                  Eligible Universities
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2730">127</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2731">
                  Across 25 countries
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-2732">
                <BuildingIcon className="w-6 h-6 text-primary-500" data-id="element-2733" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2734">
            <div className="flex items-start justify-between" data-id="element-2735">
              <div data-id="element-2736">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2737">
                  Fields of Study
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2738">48</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2739">15 priority fields</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-full" data-id="element-2740">
                <BookOpenIcon className="w-6 h-6 text-blue-500" data-id="element-2741" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2742">
            <div className="flex items-start justify-between" data-id="element-2743">
              <div data-id="element-2744">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2745">Under Review</h3>
                <div className="text-3xl font-bold text-yellow-500" data-id="element-2746">8</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2747">Pending approval</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full" data-id="element-2748">
                <GlobeIcon className="w-6 h-6 text-yellow-500" data-id="element-2749" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2750">
            <div className="flex items-start justify-between" data-id="element-2751">
              <div data-id="element-2752">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2753">Last Updated</h3>
                <div className="text-lg font-bold text-primary-500" data-id="element-2754">Today</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2755">3 changes made</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full" data-id="element-2756">
                <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-2757" />
              </div>
            </div>
          </div>
        </div>
        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200" data-id="element-2758">
          <nav className="-mb-px flex space-x-8" data-id="element-2759">
            <button onClick={() => setActiveTab('universities')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'universities' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2760">
              Eligible Universities
            </button>
            <button onClick={() => setActiveTab('fields')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'fields' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2761">
              Fields of Study
            </button>
          </nav>
        </div>
        {/* Filters and Actions */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-2762">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4" data-id="element-2763">
            <div className="relative flex-grow max-w-md" data-id="element-2764">
              <input type="text" placeholder={`Search ${activeTab === 'universities' ? 'universities' : 'fields of study'}...`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-2765" />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-2766" />
            </div>
            <button className="px-4 py-2 bg-primary-500 text-white rounded-md font-medium hover:bg-primary-600 transition-colors flex items-center" data-id="element-2767">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-2768" />
              Add{' '}
              {activeTab === 'universities' ? 'University' : 'Field of Study'}
            </button>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-2769">
            {activeTab === 'universities' && <div className="relative" data-id="element-2770">
                <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={regionFilter} onChange={e => setRegionFilter(e.target.value)} data-id="element-2771">
                  {regionOptions.map(region => <option key={region} value={region} data-id="element-2772">
                      {region}
                    </option>)}
                </select>
                <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-2773" />
              </div>}
            <div className="relative" data-id="element-2774">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-2775">
                {statusOptions.map(status => <option key={status} value={status} data-id="element-2776">
                    Status: {status}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-2777" />
            </div>
          </div>
        </div>
        {/* Universities Table */}
        {activeTab === 'universities' && <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2778">
            <div className="overflow-x-auto" data-id="element-2779">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2780">
                <thead className="bg-gray-50" data-id="element-2781">
                  <tr data-id="element-2782">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2783">
                      University
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2784">
                      Location
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2785">
                      Ranking
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2786">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2787">
                      Programs
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2788">
                      Last Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2789">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2790">
                  {universities.map(university => <tr key={university.id} className="hover:bg-gray-50" data-id="element-2791">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2792">
                        <div data-id="element-2793">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2794">
                            {university.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2795">
                            {university.accreditation}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2796">
                        <div className="text-sm text-gray-900" data-id="element-2797">
                          {university.country}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-2798">
                          {university.region}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2799">
                        <div className="text-sm text-gray-900" data-id="element-2800">
                          #{university.ranking}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2801">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(university.status)}`} data-id="element-2802">
                          {university.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2803">
                        <div className="text-sm text-gray-900" data-id="element-2804">
                          {university.programs}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2805">
                        {formatDate(university.lastUpdated)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-2806">
                        <div className="flex space-x-2" data-id="element-2807">
                          <button className="text-primary-500 hover:text-primary-700" title="View Details" data-id="element-2808">
                            <EyeIcon className="w-4 h-4" data-id="element-2809" />
                          </button>
                          <button className="text-blue-500 hover:text-blue-700" title="Edit" data-id="element-2810">
                            <EditIcon className="w-4 h-4" data-id="element-2811" />
                          </button>
                          {university.status === 'Under Review' && <>
                              <button className="text-green-500 hover:text-green-700" title="Approve" data-id="element-2812">
                                <CheckCircleIcon className="w-4 h-4" data-id="element-2813" />
                              </button>
                              <button className="text-red-500 hover:text-red-700" title="Reject" data-id="element-2814">
                                <XCircleIcon className="w-4 h-4" data-id="element-2815" />
                              </button>
                            </>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
        {/* Fields of Study Table */}
        {activeTab === 'fields' && <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2816">
            <div className="overflow-x-auto" data-id="element-2817">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2818">
                <thead className="bg-gray-50" data-id="element-2819">
                  <tr data-id="element-2820">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2821">
                      Field of Study
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2822">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2823">
                      Priority
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2824">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2825">
                      Eligible Universities
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2826">
                      Last Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2827">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2828">
                  {fieldsOfStudy.map(field => <tr key={field.id} className="hover:bg-gray-50" data-id="element-2829">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2830">
                        <div data-id="element-2831">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2832">
                            {field.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2833">
                            {field.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2834">
                        <div className="text-sm text-gray-900" data-id="element-2835">
                          {field.category}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2836">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(field.priority)}`} data-id="element-2837">
                          {field.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2838">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(field.status)}`} data-id="element-2839">
                          {field.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2840">
                        <div className="text-sm text-gray-900" data-id="element-2841">
                          {field.eligibleUniversities}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2842">
                        {formatDate(field.lastUpdated)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-2843">
                        <div className="flex space-x-2" data-id="element-2844">
                          <button className="text-primary-500 hover:text-primary-700" title="View Details" data-id="element-2845">
                            <EyeIcon className="w-4 h-4" data-id="element-2846" />
                          </button>
                          <button className="text-blue-500 hover:text-blue-700" title="Edit" data-id="element-2847">
                            <EditIcon className="w-4 h-4" data-id="element-2848" />
                          </button>
                          {field.status === 'Under Review' && <>
                              <button className="text-green-500 hover:text-green-700" title="Approve" data-id="element-2849">
                                <CheckCircleIcon className="w-4 h-4" data-id="element-2850" />
                              </button>
                              <button className="text-red-500 hover:text-red-700" title="Reject" data-id="element-2851">
                                <XCircleIcon className="w-4 h-4" data-id="element-2852" />
                              </button>
                            </>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </Layout>;
};
export default EligibleLists;